import React from 'react';
import classnames from 'classnames';
import { ApolloConsumer } from '@apollo/client';
import { Route, RouteComponentProps as RenderProps } from 'react-router';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { toGlobalId } from 'graphql-relay';

import Styled from 'styled';
import Img from 'lib/images/image-view';
import { InterscrollerContext } from 'lib/google/dfp/ad-units/interscroller';

import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import { Mention } from 'four-nets/photoblog/posts/common/comments/create/create';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';
import StaffPickBadge from 'four-nets/ui-v2/badges/staff-pick-badge/staff-pick-badge';
import Badge from 'four-nets/ui-v2/badges/badge/badge';
import { usernameIsPr } from 'four-nets/common/is-pr-article-username';

import Header from '../common/header/header';
import Footer from '../common/footer/footer';
import { Props as CommentsListProps } from '../common/comments/comments-list/comments-list';
import FeedImages from '../common/feed-images/feed-images';
import SelectionItems from 'four-nets/photoblog/posts/common/selection-items/selection-items';
import FeedImagesPreview from './feed-images-preview/feed-images-preview';
import Comments from 'four-nets/photoblog/posts/common/comments/comments';

import { PostFragment } from '../post.graphql';
import { FeedImagesFragment } from '../common/feed-images/feed-images.graphql';
import { PostType } from '../post.graphql.types';
import { CommentsQueryVariables } from '../common/comments/comments.graphql';
import fetchMoreImagesQuery, { FetchMoreImagesQuery, FetchMoreImagesQueryVariables } from './fetch-more-images.graphql';

import defaultStyles from './feed-post.scss';

export interface Props extends PostFragment, FeedImagesFragment {
    styles: typeof defaultStyles;
    refetch: () => void;
    imageId?: string;
    openComments?: boolean;
}

export interface State {
    comments?: boolean;
    commentId?: string;
    imageId?: string;
    variables?: CommentsQueryVariables;
    showMore: boolean;
    loaded?: boolean;
    commentsLoading?: boolean;
}

export interface Params {
    imageId?: string;
    slug?: string;
    postType?: string;
    username: string;
}

type User = PostFragment['user'];

const postLink = (user: User, postType: PostType, slug: string) => {
    if (user && user.profile && user.profile.isVendor) {
        if (postType === 'POST_ALBUM') {
            return `/catalog/vendor/${user.username}/blog-album/${slug}/`;
        }
        return `/catalog/vendor/${user.username}/article/${slug}/`;
    }
    if (postType === 'POST_ALBUM') {
        return `/blog/${user && user.username}/album/${slug}/`;
    }
    return `/blog/${user && user.username}/article/${slug}/`;
};

class FeedPost extends React.PureComponent<Props & RenderProps<Params>, State> {
    static contextType = InterscrollerContext;

    static getDerivedStateFromProps(props: Props & RenderProps<Params>, state: State) {
        const { hash } = props.location;

        const commentMatches = hash.match(/#comment-(\d+)/);
        let commentId: string | undefined;
        if (commentMatches) {
            commentId = toGlobalId('CommentNode', commentMatches[1]!);
        }

        let imageId;
        if (props.imageId) {
            imageId = toGlobalId('ImageNode', props.imageId);
        }

        return {
            ...state,
            comments:
                state.comments === undefined && ((commentId && !imageId) || props.openComments) ? true : state.comments,
            commentId,
            imageId,
            variables: {
                parentId: props.id,
            },
        };
    }

    state: State = { showMore: false };
    mention?: Mention;
    refMap = new Map<string, HTMLLIElement>();
    ref = React.createRef<HTMLDivElement>();

    onClick = (id?: string) => () => {
        if (id) {
            this.navigate(id);
        }
    };

    navigate = (id: string) => {
        this.props.history.push({
            pathname: `/blogs/post/${id}/`,
        });
    };

    onComments = () => {
        if (this.state.comments && this.context.updateOffsetTop) {
            setTimeout(this.context.updateOffsetTop, 20);
        }
        this.setState(prevState => {
            const newState = { comments: !prevState.comments } as State;
            if (!prevState.comments && prevState.commentsLoading === undefined) {
                newState.commentsLoading = true;
            }
            return newState;
        });
    };

    setMention = (mention: Mention) => {
        this.mention = mention;
    };

    onComment: CommentsListProps['onComment'] = ({ user }) => {
        if (user && user.username && this.mention) {
            this.mention(user.username);
        }
    };

    onAppear = (ref: HTMLLIElement | null, id: string) => {
        if (ref) {
            this.refMap.set(id, ref);
        } else {
            this.refMap.delete(id);
        }
    };

    focusComment(id: string) {
        const ref = this.refMap.get(id);

        if (ref) {
            const focus = () => {
                if (document.body.contains(ref)) {
                    if (document.activeElement === ref) {
                        ref.blur();
                        requestAnimationFrame(() => {
                            ref.focus();
                        });
                    } else {
                        ref.focus();
                    }
                } else {
                    requestAnimationFrame(focus);
                }
            };
            requestAnimationFrame(focus);
        }
    }

    componentDidMount() {
        if (this.state.commentId) {
            this.focusComment(this.state.commentId);
        }
    }

    componentDidUpdate(prevProps: Props & RenderProps<Params>, prevState: State) {
        const { commentId } = this.state;
        if (commentId && (prevState.commentId !== commentId || this.props.location !== prevProps.location)) {
            const id = commentId!;

            if (!this.state.comments) {
                this.setState(
                    {
                        comments: true,
                    },
                    () => {
                        this.focusComment(id);
                    }
                );
            } else {
                this.focusComment(id);
            }
        }
    }

    setCommentsLoading = () => {
        this.setState({ commentsLoading: false });
        if (this.context.updateOffsetTop) {
            this.context.updateOffsetTop();
        }
    };

    render() {
        const { styles, location, ...post } = this.props;
        const { variables, imageId, commentId, commentsLoading } = this.state;
        const {
            id,
            title,
            images,
            isNew,
            imagesCount,
            newImagesCount,
            postType,
            slug,
            commentCount,
            user,
            group,
            selectionItems,
            isStaffpick,
            titleImage,
            ...rest
        } = post;

        const hasNextPage = images && images.pageInfo.hasNextPage;
        const endCursor = images && images.pageInfo.endCursor;

        const isPrArticle = usernameIsPr(user?.username || '');

        return id ? (
            <React.Fragment>
                <div className={styles.post} ref={this.ref}>
                    <Header {...post} />
                    {postType === 'POST_SHORT_MESSAGE' ? (
                        <React.Fragment>
                            <p className={styles.textContent}>
                                <TextShortener text={title as string} words={this.state.showMore ? undefined : 75}>
                                    {text => (
                                        <React.Fragment>
                                            <EnhancedText
                                                followLink={
                                                    user?.profile.vendor?.isPresentationPaid ||
                                                    usernameIsPr(user?.username || '')
                                                }
                                                hashtags={true}
                                                groupId={group && group.id}
                                            >
                                                {text}
                                            </EnhancedText>
                                            {text !== title && !this.state.showMore ? (
                                                <button
                                                    className={styles.showMore}
                                                    onClick={() => this.setState({ showMore: true })}
                                                >
                                                    <FormattedMessage id='BUTTON_SHOW_MORE' />
                                                </button>
                                            ) : null}
                                        </React.Fragment>
                                    )}
                                </TextShortener>
                            </p>
                            {imagesCount && imagesCount > 1 ? (
                                <span className={styles.counter}>
                                    <FormattedMessage id='%(counter)s_PHOTOS' values={{ counter: imagesCount }} />
                                </span>
                            ) : null}
                        </React.Fragment>
                    ) : null}
                    {postType !== 'POST_ARTICLE' && images && images.edges && images.edges.length > 0 ? (
                        <ApolloConsumer>
                            {client => (
                                <FeedImages
                                    {...post}
                                    imageId={imageId}
                                    commentId={commentId}
                                    fetchMore={
                                        hasNextPage && !this.state.loaded
                                            ? () => {
                                                  this.setState({ loaded: true });
                                                  return client.query<
                                                      FetchMoreImagesQuery,
                                                      FetchMoreImagesQueryVariables
                                                  >({
                                                      query: fetchMoreImagesQuery,
                                                      variables: { id, after: endCursor },
                                                  });
                                              }
                                            : undefined
                                    }
                                    fetchMoreIndex={post.imagesCount || 0}
                                    resetFetchMore={() => {
                                        this.setState({ loaded: false });
                                    }}
                                    unshift={true}
                                    newImagesCount={newImagesCount || undefined}
                                    useExternalImgLinks
                                >
                                    {(imgRef: any, onClick: any) => (
                                        <FeedImagesPreview
                                            imgRef={imgRef}
                                            onClick={onClick}
                                            images={images}
                                            imagesCount={imagesCount}
                                            newImagesCount={newImagesCount}
                                            isVendor={Boolean(user && user.profile && user.profile.isVendor)}
                                        />
                                    )}
                                </FeedImages>
                            )}
                        </ApolloConsumer>
                    ) : null}
                    {postType === 'POST_ARTICLE' ? (
                        <div className={styles.article}>
                            {titleImage ? (
                                <Link className={styles.articleImage} to={postLink(user, postType, slug)}>
                                    <Img
                                        style={{ image: styles.image }}
                                        linkify={false}
                                        aspectFill={true}
                                        src={titleImage.src}
                                        srcSet={titleImage.srcset}
                                        sizes={{ xs: 320, md: 600 }}
                                    />
                                </Link>
                            ) : null}
                            <div className={styles.articleContent}>
                                <Link className={styles.articleLink} to={postLink(user, postType, slug)}>
                                    <h3 className={styles.albumTitle}>{title}</h3>
                                </Link>
                                {isStaffpick ? <StaffPickBadge style={{ badge: styles.new }} /> : null}
                                <p className={styles.textContent}>
                                    <EnhancedText
                                        followLink={
                                            user?.profile.vendor?.isPresentationPaid ||
                                            usernameIsPr(user?.username || '')
                                        }
                                    >
                                        {`${post.textContent}...`}
                                    </EnhancedText>
                                </p>
                            </div>
                        </div>
                    ) : null}
                    {postType === 'POST_ALBUM' ? (
                        <React.Fragment>
                            <div className={styles.spaceHolder} />
                            <a className={styles.link} href={postLink(user, postType, slug)}>
                                <h3 className={styles.albumTitle}>{title}</h3>
                            </a>
                            <span className={classnames(styles.count, { [styles.newMargin]: isNew })}>
                                (<FormattedMessage id='%(counter)s_PHOTOS' values={{ counter: imagesCount }} />)
                            </span>
                            <div className={styles.badges}>
                                {isNew ? (
                                    <Badge style={{ badge: styles.new }} uppercase={true}>
                                        <FormattedMessage id='NEW_ALBUM' />
                                    </Badge>
                                ) : null}
                                <SelectionItems selectionItems={selectionItems} collapse={true} />
                                {isStaffpick ? <StaffPickBadge style={{ badge: styles.new }} /> : null}
                            </div>
                        </React.Fragment>
                    ) : null}
                </div>
                <Footer
                    id={id}
                    user={user}
                    postType={postType}
                    commentCount={commentCount}
                    isPrArticle={isPrArticle}
                    inFeed={true}
                    {...rest}
                    onComments={this.onComments}
                    commentsLoading={commentsLoading}
                    commentsOpen={this.state.comments}
                />
                {this.state.comments ? (
                    <Comments
                        commentId={commentId}
                        commentCount={commentCount}
                        variables={variables}
                        onMount={this.setCommentsLoading}
                        onAppear={this.onAppear}
                    />
                ) : null}
            </React.Fragment>
        ) : null;
    }
}

export default Styled(defaultStyles)(props => <Route children={({ ...rest }) => <FeedPost {...rest} {...props} />} />);
